import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, AlertCircle, MessageCircle } from "lucide-react";

const STAGES = ["Lead", "Contacted", "Meeting Scheduled", "Proposal Sent", "Negotiation", "Agreement Stage", "Won", "Lost"];
const TYPES = ["School", "College", "Sports Venue", "Corporate", "Mall", "Real Estate", "Sports Operator", "Other"];

const LeadDialog = ({ users, onSaved, trigger }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    company: "", type: "Corporate", contact_name: "", phone: "", email: "", source: "",
    assigned_to: "", estimated_value: 0, status: "Lead",
    last_contacted: new Date().toISOString(), next_follow_up: "", notes: "",
  });
  const save = async () => {
    if (!f.company.trim()) return toast.error("Company required");
    const a = users.find((u) => u.id === f.assigned_to);
    try { await api.post("/leads", { ...f, assigned_to_name: a?.name || "" }); toast.success("Lead added"); setOpen(false); onSaved && onSaved(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Company / Venue name</Label><Input data-testid="lead-company-input" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></div>
          <div><Label>Type</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Status</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Contact</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Source</Label><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></div>
          <div><Label>Assigned to</Label><Select value={f.assigned_to} onValueChange={(v) => setF({ ...f, assigned_to: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Estimated Value (₹)</Label><Input type="number" value={f.estimated_value} onChange={(e) => setF({ ...f, estimated_value: +e.target.value })} /></div>
          <div><Label>Next Follow-up</Label><Input type="date" value={f.next_follow_up} onChange={(e) => setF({ ...f, next_follow_up: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="lead-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Sales() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const load = async () => {
    const [l, u] = await Promise.all([api.get("/leads"), api.get("/users")]);
    setLeads(l.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const moveStage = async (l, stage) => {
    await api.patch(`/leads/${l.id}`, { status: stage });
    load();
  };

  const openWhatsApp = async (l) => {
    const raw = (l.phone || "").replace(/[^\d+]/g, "");
    if (!raw) return toast.error("No phone number on this lead");
    const number = raw.startsWith("+") ? raw.slice(1) : (raw.startsWith("91") ? raw : `91${raw}`);
    const msg = `Hi ${l.contact_name || "there"}, this is ${l.assigned_to_name || "the GameLink team"} following up on our conversation about ${l.company}. Do you have a few minutes to chat this week?`;
    const url = `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    try {
      await api.patch(`/leads/${l.id}`, { last_contacted: new Date().toISOString() });
      load();
      toast.success("Follow-up logged");
    } catch { /* ignore */ }
  };

  const today = new Date().toISOString().slice(0, 10);
  const dueToday = leads.filter((l) => l.next_follow_up === today && !["Won", "Lost"].includes(l.status));
  const stale = leads.filter((l) => {
    if (["Won", "Lost"].includes(l.status)) return false;
    if (!l.last_contacted) return false;
    return (new Date() - new Date(l.last_contacted)) / (1000 * 60 * 60 * 24) > 7;
  });

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">CRM</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Sales / Partnerships</h1>
        </div>
        <LeadDialog users={users} onSaved={load} trigger={<Button data-testid="new-lead-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New Lead</Button>} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex justify-between mb-2"><h3 className="font-bold">Follow-ups due today</h3><AlertCircle className="h-4 w-4 text-amber-500" /></div>
          {dueToday.length === 0 ? <div className="text-sm text-slate-500">All follow-ups on track.</div> : (
            <ul className="text-sm space-y-1">{dueToday.map((l) => <li key={l.id}>{l.company} · <span className="text-slate-500">{l.assigned_to_name}</span></li>)}</ul>
          )}
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex justify-between mb-2"><h3 className="font-bold">Stale (7+ days no contact)</h3><AlertCircle className="h-4 w-4 text-rose-500" /></div>
          {stale.length === 0 ? <div className="text-sm text-slate-500">No stale leads.</div> : (
            <ul className="text-sm space-y-1">{stale.map((l) => <li key={l.id}>{l.company} · <span className="text-slate-500">{fmtDate(l.last_contacted)}</span></li>)}</ul>
          )}
        </CardContent></Card>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => (
          <div key={s} className="min-w-[280px] flex-1 bg-slate-100 rounded-md p-3">
            <div className="flex justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">{s}</div>
              <span className="text-xs text-slate-500">{leads.filter((l) => l.status === s).length}</span>
            </div>
            <div className="space-y-2">
              {leads.filter((l) => l.status === s).map((l) => (
                <Card key={l.id} data-testid={`lead-card-${l.id}`} className="border-slate-200 bg-white">
                  <CardContent className="p-3">
                    <div className="font-semibold text-sm">{l.company}</div>
                    <div className="text-xs text-slate-500">{l.contact_name} · {l.type}</div>
                    <div className="text-xs font-mono mt-1">{fmtINR(l.estimated_value)}</div>
                    <div className="mt-2 flex justify-between items-center gap-1">
                      <span className="text-xs text-slate-500 truncate">{l.assigned_to_name}</span>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`whatsapp-${l.id}`}
                          onClick={() => openWhatsApp(l)}
                          title="Send WhatsApp follow-up"
                          className="h-6 w-6 rounded flex items-center justify-center text-emerald-700 hover:bg-emerald-50 border border-emerald-200 transition-colors duration-200"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </button>
                        <Select value={l.status} onValueChange={(v) => moveStage(l, v)}>
                          <SelectTrigger className="h-6 text-xs w-28"><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
